import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check } from 'lucide-react'

const REPORT_REASONS: { value: string; label: string }[] = [
  { value: 'inappropriate', label: 'Ongepaste inhoud' },
  { value: 'not_mine', label: 'Dit is niet mijn foto' },
  { value: 'technical_issue', label: 'Technisch probleem' },
  { value: 'other', label: 'Anders' },
]

interface Props {
  open: boolean
  eventId: string
  sessionId: string
  /** Required for scope="photo"; ignored for scope="session" (the server picks
   *  a representative photo so the admin list still has a thumbnail). */
  photoId?: string
  scope: 'photo' | 'session'
  /** Only used to label the session variant ("alle 4 foto's"). */
  photoCount?: number
  onClose: () => void
}

/**
 * Bottom-sheet report form, shared by the full-screen viewer (one photo) and
 * the session overview the booth QR opens (the whole set).
 *
 * Reporting the whole session is the common case: the photos in a set are the
 * same people in the same moment, so making a guest flag them one by one is
 * busywork. The per-photo variant stays for the rare "just this one" report.
 *
 * Guest-facing text is hardcoded in Dutch, matching PhotoViewer/PublicGallery.
 */
export default function ReportSheet({
  open,
  eventId,
  sessionId,
  photoId,
  scope,
  photoCount,
  onClose,
}: Props) {
  const [reason, setReason] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  // Start clean every time the sheet opens or the thing being reported changes,
  // so a previous submission never bleeds into the next one.
  useEffect(() => {
    setReason(null)
    setNote('')
    setStatus('idle')
  }, [open, photoId, sessionId])

  // Escape closes the sheet, but never mid-send.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && status !== 'sending') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, status, onClose])

  const submit = async () => {
    if (!reason || status === 'sending') return
    setStatus('sending')
    try {
      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          sessionId,
          scope,
          photoId: scope === 'photo' ? photoId : undefined,
          reason,
          note: note.trim() || undefined,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setStatus('sent')
      setTimeout(onClose, 1600)
    } catch (err) {
      console.error('Report failed:', err)
      setStatus('error')
    }
  }

  const isSession = scope === 'session'
  const title = isSession ? "Foto's melden" : 'Foto melden'
  const subtitle = isSession
    ? photoCount
      ? `Waarom meld je deze sessie? Dit geldt voor alle ${photoCount} foto's.`
      : 'Waarom meld je deze sessie? Dit geldt voor alle foto’s in deze set.'
    : 'Waarom meld je deze foto?'

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
          onClick={(e) => {
            // The session variant sits inside a lightbox that closes on
            // backdrop click — don't let a tap here dismiss both layers.
            e.stopPropagation()
            if (status !== 'sending') onClose()
          }}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="w-full max-w-sm rounded-t-3xl bg-[#141420] p-5 text-left sm:rounded-3xl"
            style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            {status === 'sent' ? (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
                  <Check className="h-6 w-6 text-emerald-400" />
                </div>
                <p className="font-semibold text-white">Bedankt voor je melding</p>
                <p className="text-sm text-white/50">We bekijken het zo snel mogelijk.</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-white">{title}</h3>
                <p className="mt-1 text-sm text-white/50">{subtitle}</p>

                <div className="mt-4 space-y-2">
                  {REPORT_REASONS.map((r) => (
                    <button
                      key={r.value}
                      onClick={() => setReason(r.value)}
                      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
                        reason === r.value
                          ? 'border-white/40 bg-white/10 text-white'
                          : 'border-white/10 text-white/70 active:bg-white/5'
                      }`}
                    >
                      <span
                        className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                          reason === r.value ? 'border-white bg-white' : 'border-white/30'
                        }`}
                      />
                      {r.label}
                    </button>
                  ))}
                </div>

                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 500))}
                  placeholder="Extra toelichting (optioneel)"
                  rows={2}
                  className="mt-3 w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/30"
                />

                {status === 'error' && (
                  <p className="mt-2 text-sm text-red-400">Er ging iets mis. Probeer het opnieuw.</p>
                )}

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={onClose}
                    className="h-12 flex-1 rounded-2xl border border-white/15 text-sm font-semibold text-white/70 active:bg-white/5 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={submit}
                    disabled={!reason || status === 'sending'}
                    className="h-12 flex-1 rounded-2xl bg-white text-sm font-bold text-black disabled:opacity-40 active:scale-[0.98] transition-transform"
                  >
                    {status === 'sending' ? 'Versturen…' : 'Versturen'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
